/**
 * This module provides a pair of functions, extractChildren and mergeChildren, which perform one level of
 * data normalization.
 *
 * Given the hierarchy of A has B children, which has C children, fetchA(5) would return data about an A,
 * which has a list of B children. extractChildren can replace the B children on A with a list of ids, and
 * merge the B children into a separate B map. mergeChildren performs the inverse operation--replacing the
 * list of ids on a normalized A with the associated children from a B map.
 *
 * The generic APIs intentionally preserve legacy caller types. Precisely modeling normalized and expanded
 * properties would require a broader redesign of the workflow store types.
 */
import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as logging from 'util/Logging';

/** Dynamic object shape used internally while transforming keyed properties. */
type StoreObject = Record<string, unknown>;

/** Internal lookup from child property names to child IDs and objects. */
type ChildMap = Record<string, Record<string, StoreObject>>;

/** Child ID types supported by existing StoreTransform callers. */
type ChildId = string | number;

/*
 * Given an object with potentially multiple lists of children, strip the children into separate objects,
 * replacing them with a list of ids instead.
 *
 *    obj: an object whose keys are a superset of childMap's keys.
 *    childMap: an object mapping keys to objects.
 *
 *    Returns: a two-element list of obj and children
 *      obj: an object with the same keys as the original argument, but with the children replaced with
 *           lists of ids.
 *      children: an object mapping the same keys as childMap to the same maps updated with the new
 *           children.
 *
 * For example:
 *
 * > obj = {name: 'John', cats: [{id: 1, name: 'Mr. Wiggles'}]}
 * > childMap={cats: {2: {id: 2, name: 'Meow'}}}
 * > extractChildren(obj, childMap)
 *   [
 *     {name: 'John', cats: [1]},
 *     {
 *       cats: {
 *         1: {id: 1, name: 'Mr. Wiggles'},
 *         2: {id: 2, name: 'Meow'},
 *       }
 *     }
 *   ]
 */
export function extractChildren<TParent extends object, TChildren extends object>(
  obj: TParent,
  childMap: TChildren
): [TParent, TChildren] {
  const parent = {
    ...obj,
  } as StoreObject;
  const children: ChildMap = {};
  // Callers use named interfaces that do not have ChildMap's string index signature.
  const childMapByKey = childMap as unknown as ChildMap;
  for (const key of Object.keys(childMapByKey)) {
    assert(parent[key] != null, `extractChildren did not find child array at "${key}"`);
    const newChildren = (parent[key] as Array<StoreObject>).map((child) => ({
      ...child,
    }));
    children[key] = reactUpdate(childMapByKey[key], {$merge: _.indexBy(newChildren, 'id')});
    // eslint-disable-next-line no-restricted-syntax
    parent[key] = _.pluck(newChildren, 'id');
  }

  // Preserve legacy caller types because normalized store properties are not modeled separately.
  return [parent as unknown as TParent, children as unknown as TChildren];
}

/*
 * Given an object with possibly multiple lists of child ids, replace each list of ids with a list of
 * children pulled from the corresponding entry in childMap.
 *
 *    obj: an object whose keys are a superset of childMap's keys.
 *    childMap: an object mapping keys to objects.
 *
 *    Returns: an object with the same keys as obj.
 *
 * For example:
 *
 * > obj = {name: 'John', cats: [1]}
 * > childMap = {cats: {1: {id: 1, name: 'Mr. Wiggles'}}}
 * > mergeChildren(obj, childMap)
 *   {
 *     name: 'John',
 *     cats: [{id: 1, name: 'Mr. Wiggles'}],
 *   }
 */
export function mergeChildren<TParent extends object>(obj: TParent, childMap: object): TParent {
  // Callers use named interfaces that do not have ChildMap's string index signature.
  const childMapByKey = childMap as unknown as ChildMap;
  // Underscore cannot preserve TParent through dynamic child replacement.
  return _.mapObject(obj as StoreObject, (val, key) => {
    // eslint-disable-next-line benchlint/no-in-operator
    if (key in childMapByKey) {
      return (val as Array<ChildId>)
        .map((childId) => {
          if (childMapByKey[key][childId] == null) {
            logging.warn(`${childId} not present in ${key} childMap: ${childMapByKey[key]}`);
          }
          return childMapByKey[key][childId];
        })
        .filter((child) => child != null);
    }
    return val;
  }) as unknown as TParent;
}
